import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import Link from "next/link";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { supabase } from "@/lib/supabaseClient";
import { createCheckoutSession, fetchUsage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const tiers = [
  {
    name: "Free",
    price: "$0",
    period: "",
    images: "20",
    features: [
      "20 images per month",
      "Transparent PNG export",
      "AI-powered removal",
    ],
    cta: "Get started free",
    tier: "free",
    highlight: false,
  },
  {
    name: "Pro",
    price: "$9",
    period: "/mo",
    images: "100",
    features: [
      "100 images per month",
      "Transparent PNG export",
      "AI-powered removal",
      "Priority processing",
    ],
    cta: "Upgrade to Pro",
    tier: "pro",
    highlight: true,
  },
  {
    name: "Business",
    price: "$29",
    period: "/mo",
    images: "500",
    features: [
      "500 images per month",
      "Transparent PNG export",
      "AI-powered removal",
      "Priority processing",
      "Bulk processing",
    ],
    cta: "Upgrade to Business",
    tier: "business",
    highlight: false,
  },
];

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function Pricing() {
  const router = useRouter();
  const [email, setEmail] = useState<string | undefined>();
  const [currentTier, setCurrentTier] = useState<string>("free");
  const [loadingTier, setLoadingTier] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setEmail(data.session.user.email ?? undefined);
      }
    });
  }, []);

  useEffect(() => {
    if (!email) return;
    supabase.auth.getSession().then(async ({ data }) => {
      const token = data.session?.access_token;
      if (!token) return;
      try {
        const usage = await fetchUsage(token);
        setCurrentTier(usage.tier);
      } catch {
        // Silently fail — tier defaults to "free"
      }
    });
  }, [email]);

  const handleUpgrade = async (tier: string) => {
    if (tier === "free") {
      router.push(email ? "/dashboard" : "/login");
      return;
    }

    if (!email) {
      router.push("/login");
      return;
    }

    setLoadingTier(tier);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      trackEvent("Upgrade Clicked", { tier });
      const { url } = await createCheckoutSession(tier, token);
      window.location.href = url;
    } catch (err: any) {
      alert(err.message || "Something went wrong");
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <>
      <Head>
        <title>Pricing - ClearCut</title>
        <meta name="description" content="Simple, transparent pricing for ClearCut. Start free with 20 images/month. Upgrade to Pro ($9/mo) or Business ($29/mo) for more." />
      </Head>
      <div className="min-h-screen bg-bg text-white">
        <Navbar email={email} />

        {/* Accent glow blob */}
        <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[500px] w-[800px] rounded-full bg-accent/[0.03] blur-[120px]" />

        <motion.div
          className="relative mx-auto max-w-5xl px-6 pb-32 pt-24 text-center"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-1.5 text-xs font-medium text-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
              Simple, transparent pricing
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="mt-8 font-display text-4xl font-extrabold tracking-tight sm:text-5xl"
          >
            Choose your plan
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mx-auto mt-4 max-w-md text-lg text-muted"
          >
            Start free, upgrade when you need more. Cancel anytime.
          </motion.p>

          {/* Pricing cards */}
          <motion.div
            variants={stagger}
            className="mt-16 grid gap-6 text-left sm:grid-cols-3"
          >
            {tiers.map((t) => {
              const isCurrent = email && currentTier === t.tier;
              const isDowngrade =
                email &&
                ((currentTier === "business" && t.tier !== "business") ||
                  (currentTier === "pro" && t.tier === "free"));

              return (
                <motion.div
                  key={t.tier}
                  variants={fadeUp}
                  whileHover={{ y: -4 }}
                  className={`relative flex flex-col rounded-xl border p-6 transition-colors ${
                    t.highlight
                      ? "border-accent/40 bg-surface"
                      : "border-border bg-surface/50"
                  }`}
                >
                  {t.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-3 py-0.5 text-xs font-bold text-black">
                      Most popular
                    </span>
                  )}

                  <h3 className="font-display text-lg font-bold">{t.name}</h3>

                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-display text-4xl font-extrabold">
                      {t.price}
                    </span>
                    {t.period && (
                      <span className="text-sm text-muted">{t.period}</span>
                    )}
                  </div>

                  <p className="mt-2 text-sm text-muted">
                    {t.images} images per month
                  </p>

                  <ul className="mt-6 flex-1 space-y-3">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted">
                        <svg
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={!!isCurrent || !!isDowngrade || loadingTier === t.tier}
                    onClick={() => handleUpgrade(t.tier)}
                    className={`mt-8 w-full rounded-lg py-3 text-sm font-semibold transition-colors ${
                      isCurrent
                        ? "cursor-default border border-border bg-surface text-muted"
                        : isDowngrade
                          ? "cursor-not-allowed border border-border bg-surface/50 text-muted-dim"
                          : t.highlight
                            ? "bg-accent text-black hover:bg-accent-dim"
                            : "border border-border text-white hover:border-border-light"
                    }`}
                  >
                    {loadingTier === t.tier
                      ? "Redirecting..."
                      : isCurrent
                        ? "Current plan"
                        : isDowngrade
                          ? "Manage in portal"
                          : t.cta}
                  </motion.button>
                </motion.div>
              );
            })}
          </motion.div>

          {email && currentTier !== "free" && (
            <motion.p variants={fadeUp} className="mt-8 text-sm text-muted">
              Need to change or cancel?{" "}
              <Link href="/dashboard" className="text-accent hover:underline">
                Manage your subscription
              </Link>{" "}
              from the dashboard.
            </motion.p>
          )}
        </motion.div>

        <Footer />
      </div>
    </>
  );
}
